/**
 * Shared renderer for NAATI dynamic routes. Consumes the deterministic
 * content object built by generateNaatiPage() and outputs semantic HTML
 * with schema-friendly landmarks (article, section, dl, ol).
 */
import { Link } from "@tanstack/react-router";
import type { NaatiPageContent } from "@/lib/naati/generator";
import type { NaatiCity, NaatiDocType } from "@/data/naati";
import { RelatedClusters } from "@/components/RelatedClusters";
import { OfficialServiceSources } from "@/components/OfficialServiceSources";

export function NaatiPageBody(props: {
  content: NaatiPageContent;
  service?: NaatiDocType;
  city?: NaatiCity;
  breadcrumb?: Array<{ label: string; to?: string }>;
  /** Set when the parent route already renders the page's single <h1>. */
  demoteHeading?: boolean;
}) {
  const { content, service, city, breadcrumb, demoteHeading } = props;
  // Exactly one <h1> per document: demote to <h2> when the route owns the H1.
  const Heading = (demoteHeading ? "h2" : "h1") as "h1" | "h2";
  return (
    <article className="mx-auto max-w-4xl px-4 py-10 prose prose-slate max-w-none">
      {breadcrumb && (
        <nav className="text-sm text-slate-500 mb-4" aria-label="Breadcrumb">
          {breadcrumb.map((b, i) => (
            <span key={i}>
              {i > 0 && " › "}
              {b.to ? <Link to={b.to}>{b.label}</Link> : <span>{b.label}</span>}
            </span>
          ))}
        </nav>
      )}
      <Heading className="text-3xl md:text-4xl font-bold text-slate-900">{content.h1}</Heading>

      <p
        className="speakable-summary lead answer-summary text-lg text-slate-700"
        dangerouslySetInnerHTML={{ __html: content.intro }}
      />

      {/* Benefits table */}
      <section aria-labelledby="benefits">
        <h2 id="benefits">จุดเด่นบริการแปล NAATI ของเรา</h2>
        <dl className="grid gap-4 md:grid-cols-2">
          {content.benefits.map(([k, v], i) => (
            <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <dt className="font-semibold text-slate-900">{k}</dt>
              <dd className="text-slate-700 mt-1">{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* Steps */}
      <section aria-labelledby="steps">
        <h2 id="steps">ขั้นตอนการสั่งงานแปล NAATI</h2>
        <ol className="space-y-3">
          {content.steps.map((s, i) => (
            <li key={i} className="rounded border-l-4 border-blue-600 bg-blue-50 p-3">
              <strong className="text-blue-900">
                {i + 1}. {s.tag} —
              </strong>{" "}
              <span className="text-slate-800">{s.text}</span>
            </li>
          ))}
        </ol>
      </section>

      {/* Rotated H2 sections */}
      {content.sections.map((sec, i) => (
        <section key={i} aria-label={sec.heading}>
          <h2>{sec.heading}</h2>
          <div dangerouslySetInnerHTML={{ __html: sec.html }} />
        </section>
      ))}

      {/* Doc-specific field checklist */}
      {service && (
        <section aria-labelledby="fields">
          <h2 id="fields">ข้อมูลใน {service.nameTh} ที่นักแปลต้องตรวจเทียบกับต้นฉบับ</h2>
          <ul>
            {service.keyFields.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
          <p>ใช้บ่อยสำหรับ: {service.commonUse.join(" · ")}</p>
        </section>
      )}

      {/* Local proof */}
      {city && (
        <section aria-labelledby="local">
          <h2 id="local">
            ทำไมลูกค้าใน {city.nameTh} ({city.nameEn}) เลือกเรา
          </h2>
          <ul>
            <li>Timezone {city.timezone} — เรานัดคุยและตอบ LINE ตามเวลาลูกค้า</li>
            <li>
              Courier lane $
              {city.courierDays === 0
                ? "ส่งไปรษณีย์ในวันเดียว (in-country)"
                : `DHL Express ${city.courierDays} วันทำการ`}
            </li>
            {city.embassyNote && (
              <li>ประสานงานกับ {city.embassyNote} ได้ตรง — ทราบ slot การนัดล่าสุด</li>
            )}
            <li>เข้าใจข้อกำหนดของหน่วยงานปลายทางใน {city.region ?? city.country}</li>
          </ul>
        </section>
      )}

      {/* FAQ */}
      <section
        aria-labelledby="faq"
        style={{ contentVisibility: "auto", containIntrinsicSize: "600px 800px" }}
      >
        <h2 id="faq">คำถามที่พบบ่อย</h2>
        <div className="space-y-4">
          {content.faqs.map((f, i) => (
            <details key={i} className="rounded border border-slate-200 p-4">
              <summary className="font-semibold cursor-pointer text-slate-900">{f.q}</summary>
              <p className="mt-2 text-slate-700 faq-answer">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mt-10 rounded-lg bg-blue-600 p-6 text-white">
        <h2 className="text-white text-2xl">พร้อมสั่งงานแปล NAATI ตอนนี้</h2>
        <p className="text-blue-100">
          ส่งไฟล์ที่อ่านชัดและระบุหน่วยงานผู้รับ เพื่อให้ทีมงานตรวจประเภทการรับรองและเสนอราคา
        </p>
        <div className="not-prose mt-4 flex flex-wrap gap-2">
          <a href="tel:+66948958999" className="rounded-md bg-background px-4 py-2 text-sm font-semibold text-foreground">โทร 094-895-8999</a>
          <a href="https://line.me/R/ti/p/@thainotarylaw" target="_blank" rel="noopener noreferrer" className="rounded-md border border-background/60 px-4 py-2 text-sm font-semibold text-white">LINE</a>
          <Link to="/contact" className="rounded-md border border-background/60 px-4 py-2 text-sm font-semibold text-white">ติดต่อสำนักงาน</Link>
        </div>
      </section>

      <OfficialServiceSources theme="naati" />
      <RelatedClusters theme="naati" />

      <p className="mt-6 text-xs text-slate-400">
        เนื้อหาถูกจัดเรียงเฉพาะสำหรับหน้านี้ (page seed) — ไม่ถูก duplicate ไปหน้าอื่น • Word count:
        ~{content.wordCount} คำ
      </p>
    </article>
  );
}
